/**
 * 생기부 검사기 API
 * Backend: /api/inspector/*
 */

import { auth } from '../firebase/config';
import { getApiUrl } from '../utils/apiConfig';
import { isRateLimitError, getRateLimitMessage } from '../utils/rateLimitHandler';
import { fetchWithRetry, calcApiTimeout } from '../utils/fetchWithRetry';
import { assertInspectorContextResponse } from '../utils/inspectorContextContract';
import { readSseJson } from '../utils/sseJsonStream';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// 인증 헤더 생성 (saenggibuApi와 동일한 패턴)
const getAuthHeaders = async () => {
  const user = auth.currentUser;
  
  if (!user) {
    return {
      'Content-Type': 'application/json',
    };
  }

  try {
    const token = await user.getIdToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'X-User-ID': user.uid,
    };
  } catch (error) {
    console.error('토큰 가져오기 실패:', error);
    return {
      'Content-Type': 'application/json',
      'X-User-ID': user.uid,
    };
  }
};

// API 요청 헬퍼 함수 (saenggibuApi와 동일한 패턴)
const apiRequest = async (url, options = {}) => {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(getApiUrl(url), {
      ...options,
      headers: {
        ...authHeaders,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.detail || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

const apiRequestWithRetry = async (url, options = {}, retryConfig = {}) => {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetchWithRetry(getApiUrl(url), {
      ...options,
      headers: {
        ...authHeaders,
        ...options.headers,
      },
    }, retryConfig);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.detail || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API request with retry failed:', error);
    throw error;
  }
};

/**
 * 한 줄 생기부 문장 검사 (2단계: 규칙 + 기재요령)
 * @param {string} text - 검사할 문장
 * @param {string} schoolLevel - 학교급 (elementary/middle/high)
 * @param {string} selectedModel - 선택된 모델 (gemini-2.5-flash-lite/gemini-2.5-flash)
 * @param {number} academicYear - 기록 작성 학년도
 * @param {number} recordGrade - 기록 작성 당시 학년
 * @returns {Promise} 검사 결과 (basic_violations + guideline_violations)
 */
export const checkSingleSentence = async (
  text,
  schoolLevel = 'elementary',
  selectedModel = 'gemini-2.5-flash-lite',
  academicYear = 2026,
  recordGrade = null,
) => {
  try {
    const response = await apiRequestWithRetry('/api/inspector/check-single', {
      method: 'POST',
      body: JSON.stringify({
        text: text.trim(),
        school_level: schoolLevel,
        selected_model: selectedModel,
        academic_year: academicYear,
        record_grade: recordGrade,
      })
    }, {
      maxRetries: 1,
      timeoutMs: calcApiTimeout('single-inspector'),
      retryOnTimeout: false
    });
    return assertInspectorContextResponse(response, {
      academicYear,
      recordGrade,
    });
  } catch (error) {
    console.error('생기부 검사 API 오류:', error);
    
    if (error.message?.includes('403')) {
      throw new Error('일일 무료 사용량을 초과했습니다. PRO 플랜을 이용해주세요.');
    }
    
    // Rate Limit 에러 통일 처리
    if (isRateLimitError(error)) {
      throw new Error(getRateLimitMessage({ includeLink: true, html: false }));
    }

    if (
      error.name === 'AbortError' ||
      error.message?.includes('Load failed') ||
      error.message?.includes('Failed to fetch') ||
      error.message?.includes('NetworkError') ||
      error.message?.includes('초과')
    ) {
      throw new Error('검사가 예상보다 오래 걸리고 있어요. 잠시 후 다시 시도해주세요.');
    }
    
    throw error;
  }
};

// ========================================
// 일괄 검사 스트리밍 (하트비트 + 멱등성 + 자동 재접속)
// 배경: 일괄 검사는 요청 1개가 2~5분 무음이라 학교망 유휴 킬(약 3분)에 끊겼고,
// 자동 재시도가 같은 검사를 중복 실행했다 (3일 실측 재전송률 7.8%, 전 기능 1위).
// ========================================

// 서버가 최소 10초 간격으로 진행/하트비트 이벤트를 보내므로 45초 무음이면 연결 유실로 판단
const INSPECTOR_STREAM_INACTIVITY_TIMEOUT_MS = 45_000;
const INSPECTOR_STREAM_MAX_CONNECTION_ATTEMPTS = 5;
const INSPECTOR_STREAM_RECONNECT_DELAY_MS = 2_000;

const isAbortError = (error) =>
  error?.name === 'AbortError' || error?.message?.includes('aborted');

const isNetworkishInspectorError = (error) => {
  const message = error?.message || '';
  return (
    message.includes('Failed to fetch') ||
    message.includes('NetworkError') ||
    message.includes('Load failed') ||
    message.includes('INSPECTOR_STREAM_INACTIVITY_TIMEOUT') ||
    message.includes('INSPECTOR_STREAM_ENDED_WITHOUT_COMPLETE') ||
    message.includes('network error')
  );
};

/**
 * 일괄 검사 오류를 사용자에게 보여줄 수 있는 문구로 변환.
 * 원문 네트워크 에러("Failed to fetch" 등)를 그대로 노출하지 않는다.
 */
export const getFriendlyInspectorErrorMessage = (error) => {
  if (error?.isServerReportedError || error?.isHttpError) {
    return error.message || '일괄 검사 중 문제가 생겼어요. 잠시 후 다시 시도해주세요.';
  }
  if (isNetworkishInspectorError(error)) {
    return '네트워크 연결이 불안정해요. 검사가 완료되었다면 검사 기록에 자동 저장되니 잠시 후 확인해주세요.';
  }
  return error?.message || '일괄 검사 중 문제가 생겼어요. 잠시 후 다시 시도해주세요.';
};

/** 스트림 응답 본문을 읽어 progress 콜백을 호출하고 complete 페이로드를 반환 */
const readInspectorCheckStream = async (response, { onProgress } = {}) => {
  const events = readSseJson(response.body, {
    idleTimeoutMs: INSPECTOR_STREAM_INACTIVITY_TIMEOUT_MS,
    idleTimeoutError: () => new Error('INSPECTOR_STREAM_INACTIVITY_TIMEOUT'),
    cancelOnExit: true,
  });
  for await (const parsed of events) {
    if (parsed.type === 'start' || parsed.type === 'progress') {
      onProgress?.(parsed);
    } else if (parsed.type === 'complete') {
      return parsed;
    } else if (parsed.type === 'cancelled') {
      return {
        success: false,
        cancelled: true,
        message: parsed.message || '검사를 중단했어요.',
      };
    } else if (parsed.type === 'error') {
      const serverError = new Error(parsed.message || '일괄 검사 중 문제가 생겼어요.');
      // 서버가 판단한 실패 → 재접속해도 같은 결과이므로 즉시 실패 처리
      serverError.isServerReportedError = true;
      throw serverError;
    }
  }
  // complete 없이 스트림이 끝남 → 연결 유실로 간주해 재접속 유도
  throw new Error('INSPECTOR_STREAM_ENDED_WITHOUT_COMPLETE');
};

/**
 * 일괄 검사 (하트비트 스트리밍 + 자동 재접속 + 서버 멱등성)
 * @param {Object} params
 * @param {Array} params.students - [{name, content}, ...]
 * @param {string} params.schoolLevel - elementary/middle/high
 * @param {string} params.selectedModel - 검사 모델
 * @param {number} params.academicYear - 기록 작성 학년도
 * @param {number} params.recordGrade - 기록 작성 당시 학년
 * @param {'uniform'|'mixed'} params.contextMode - 일괄 기준 모드
 * @param {string} params.jobId - 멱등성 잡 ID (재접속/복구 시 같은 값 재사용)
 * @param {Function} params.onProgress - 진행 이벤트 콜백
 * @param {AbortSignal} params.signal - 중단 시그널
 * @returns {Promise<Object>} legacy check-batch 와 같은 형태 {success, record_id, results, summary}
 */
export const checkBatchStream = async ({
  students,
  schoolLevel = 'elementary',
  selectedModel = 'gemini-2.5-flash-lite',
  academicYear = 2026,
  recordGrade = null,
  contextMode = 'uniform',
  jobId,
  onProgress,
  signal,
} = {}) => {
  const body = JSON.stringify({
    students,
    school_level: schoolLevel,
    selected_model: selectedModel,
    academic_year: academicYear,
    record_grade: recordGrade,
    context_mode: contextMode,
    check_job_id: jobId,
  });

  let lastError = null;
  for (let attempt = 1; attempt <= INSPECTOR_STREAM_MAX_CONNECTION_ATTEMPTS; attempt++) {
    if (signal?.aborted) {
      const abortError = new Error('검사를 중단했어요.');
      abortError.name = 'AbortError';
      throw abortError;
    }
    if (attempt > 1) {
      onProgress?.({ type: 'progress', stage: 'reconnecting', attempt });
      await new Promise(resolve => setTimeout(resolve, INSPECTOR_STREAM_RECONNECT_DELAY_MS));
    }

    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(getApiUrl('/api/inspector/check-batch-stream'), {
        method: 'POST',
        headers: { ...authHeaders, 'Accept': 'text/event-stream' },
        body,
        credentials: 'omit',
        mode: 'cors',
        signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const httpError = new Error(errorData.detail || `HTTP error! status: ${response.status}`);
        httpError.isHttpError = true;
        httpError.status = response.status;
        // 4xx(인증/권한/요청 오류)는 재접속해도 같은 결과 → 즉시 실패
        if (response.status >= 400 && response.status < 500) {
          throw httpError;
        }
        lastError = httpError;
        continue;
      }

      const result = await readInspectorCheckStream(response, { onProgress });
      return assertInspectorContextResponse(result, {
        academicYear,
        contextMode,
        recordGrade,
        students,
      });
    } catch (error) {
      if (signal?.aborted || isAbortError(error)) {
        throw error;
      }
      if (error.isServerReportedError || (error.isHttpError && error.status < 500)) {
        throw error;
      }
      // 네트워크성 오류/침묵 타임아웃 → 같은 jobId 로 재접속
      // (서버 멱등성 가드가 실행 중 작업 합류/저장된 결과 재사용을 보장)
      console.warn(`일괄 검사 스트림 연결 유실 (시도 ${attempt}/${INSPECTOR_STREAM_MAX_CONNECTION_ATTEMPTS}):`, error?.message);
      lastError = error;
    }
  }
  throw lastError || new Error('네트워크 연결이 불안정해요.');
};

/**
 * 일괄 검사 잡 상태 조회 (네트워크 끊김 후 저장된 결과 복구용)
 * @param {string} jobId - 검사 잡 ID
 * @returns {Promise} {status, record_id, running_fresh, ...}
 */
export const getInspectorCheckJobStatus = async (jobId) => {
  return await apiRequest(`/api/inspector/check-jobs/${encodeURIComponent(jobId)}`);
};

/**
 * 헬스 체크
 * @returns {Promise} 서비스 상태
 */
export const healthCheck = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/inspector/health`);
    return response.data;
  } catch (error) {
    console.error('헬스 체크 오류:', error);
    throw error;
  }
};

/**
 * 검사 기록 조회
 * @param {number} limit - 가져올 기록 수
 * @param {number} offset - 시작 위치
 * @returns {Promise} 검사 기록 목록
 */
export const getInspectorHistory = async (limit = 100, offset = 0) => {
  return await apiRequest(`/api/inspector/history?limit=${limit}&offset=${offset}`);
};

/**
 * 검사 기록 상세 조회 (saenggibuApi와 동일한 패턴)
 * @param {string} recordId - 기록 ID
 * @returns {Promise} 검사 기록 상세
 */
export const getInspectorRecord = async (recordId) => {
  return await apiRequest(`/api/inspector/history/${recordId}`);
};

/**
 * 엑셀 다운로드용 검사 기록 상세 일괄 조회
 * @param {string[]} recordIds - 기록 ID 목록
 * @returns {Promise} 검사 기록 상세 목록
 */
export const getInspectorHistoryExportData = async (recordIds) => {
  return await apiRequest('/api/inspector/history/export-data', {
    method: 'POST',
    body: JSON.stringify({ record_ids: recordIds }),
  });
};

/**
 * 검사 결과 AI 수정안 생성
 * @param {object} payload - current_content, violations, instruction 등
 * @returns {Promise} AI 수정 결과
 */
export const refineInspectorContent = async (payload) => {
  return await apiRequestWithRetry('/api/inspector/refine', {
    method: 'POST',
    body: JSON.stringify(payload)
  }, {
    maxRetries: 1,
    timeoutMs: calcApiTimeout('inspector-refine')
  });
};

/**
 * 단일 검사 기록의 확정 수정안 저장
 * @param {string} recordId - 기록 ID
 * @param {string} refinedSentence - 확정 수정안
 * @param {object|null} revision - 수정 이력 메타
 * @returns {Promise} 저장 결과
 */
export const updateInspectorRecord = async (recordId, refinedSentence, revision = null) => {
  return await apiRequest(`/api/inspector/history/${recordId}`, {
    method: 'PATCH',
    body: JSON.stringify({ refined_sentence: refinedSentence, revision })
  });
};

/**
 * 일괄 검사 기록의 학생별 확정 수정안 저장
 * @param {string} recordId - 기록 ID
 * @param {number} studentIndex - 학생 인덱스
 * @param {string} refinedSentence - 확정 수정안
 * @param {object|null} revision - 수정 이력 메타
 * @returns {Promise} 저장 결과
 */
export const updateBatchInspectorResult = async (recordId, studentIndex, refinedSentence, revision = null) => {
  return await apiRequest(`/api/inspector/history/${recordId}/batch-result/${studentIndex}`, {
    method: 'PATCH',
    body: JSON.stringify({ refined_sentence: refinedSentence, revision })
  });
};

/**
 * 일괄 검사 기록의 학생 이름 저장
 * @param {string} recordId - 기록 ID
 * @param {number} studentIndex - 학생 인덱스
 * @param {string} studentName - 저장할 학생 이름
 * @returns {Promise} 저장 결과
 */
export const updateBatchInspectorStudentName = async (recordId, studentIndex, studentName) => {
  return await apiRequest(`/api/inspector/history/${recordId}/batch-result/${studentIndex}/student-name`, {
    method: 'PATCH',
    body: JSON.stringify({ student_name: studentName })
  });
};

/**
 * 검사 기록의 메타 정보 저장
 * @param {string} recordId - 기록 ID
 * @param {object} meta - custom_title 등 기록 메타
 * @returns {Promise} 저장 결과
 */
export const updateInspectorRecordMeta = async (recordId, meta) => {
  return await apiRequest(`/api/inspector/history/${recordId}/meta`, {
    method: 'PATCH',
    body: JSON.stringify(meta)
  });
};

/**
 * 검사 기록 삭제 (saenggibuApi와 동일한 패턴)
 * @param {string} recordId - 기록 ID
 * @returns {Promise} 삭제 결과
 */
export const deleteInspectorRecord = async (recordId) => {
  return await apiRequest(`/api/inspector/history/${recordId}`, {
    method: 'DELETE'
  });
};

const inspectorApi = {
  checkSingleSentence,
  checkBatchStream,
  getInspectorCheckJobStatus,
  getFriendlyInspectorErrorMessage,
  healthCheck,
  getInspectorHistory,
  getInspectorRecord,
  getInspectorHistoryExportData,
  refineInspectorContent,
  updateInspectorRecord,
  updateBatchInspectorResult,
  updateBatchInspectorStudentName,
  updateInspectorRecordMeta,
  deleteInspectorRecord
};

export default inspectorApi;

